import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';
import styled, { withTheme } from 'styled-components';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import {
  buildSocChartXAxisTicks,
  padSocSegmentForVictoryArea,
  splitSocChartDataIntoContinuousSegments,
} from '../../../utils/charts';
import { getDateLabel } from '../../../utils/dateLabel';
import { addHours, endOfUKDay, formatHHmmInUK, startOfUKDay } from '../../../utils/dateUtils';
import LoadingCard from '../../LoadingCard';
import { NavArrowButton } from '../../Styled';
import StyledTitle from '../../Styled/StyledTitle';

const ChartHeaderNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const toDate = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? new Date() : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const BatteryStateOfCharge = ({
  data,
  fetchingAnalyticsData,
  theme,
  onPreviousDay,
  onNextDay,
  isAtMinDate,
  isAtMaxDate,
  selectedDate,
}) => {
  const dataAvailable = data?.length > 0;
  const dateLabel = getDateLabel(selectedDate);

  const selectedDay = useMemo(() => toDate(selectedDate), [selectedDate]);

  const domainX = useMemo(() => [startOfUKDay(selectedDay), endOfUKDay(selectedDay)], [selectedDay]);

  const socSegments = useMemo(
    () => (dataAvailable ? splitSocChartDataIntoContinuousSegments(data) : []),
    [data, dataAvailable]
  );

  const { lastTimeLabel, xAxisTicks } = useMemo(() => {
    const lastPoint = dataAvailable ? data[data.length - 1] : null;
    const lastTimeLabel = lastPoint?.timeLabel;

    const xAxisTicks = buildSocChartXAxisTicks(domainX[0], domainX[1], lastPoint);

    return { lastTimeLabel, xAxisTicks };
  }, [domainX, dataAvailable, data]);

  const placeholderSocData = useMemo(() => {
    const start = startOfUKDay(selectedDay);
    return [0, 3, 6, 9, 12, 15, 18, 21, 23].map((h) => {
      const x = addHours(start, h);
      return { x, timeLabel: formatHHmmInUK(x), y: 0 };
    });
  }, [selectedDay]);

  const yDomain = dataAvailable ? undefined : [0, 100];

  const tickTimeString = (t) => {
    const d = t instanceof Date ? t : new Date(t);
    return formatHHmmInUK(d);
  };

  return (
    <>
      {/* Header section with navigation arrows and title */}
      <ChartHeaderNav style={{ marginTop: 60, marginBottom: 0 }}>
        <NavArrowButton
          onClick={onPreviousDay}
          disabled={isAtMinDate || fetchingAnalyticsData}
          aria-label="Previous day"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </NavArrowButton>

        <StyledTitle>Battery state of charge {dateLabel}</StyledTitle>

        <NavArrowButton onClick={onNextDay} disabled={isAtMaxDate || fetchingAnalyticsData} aria-label="Next day">
          <FontAwesomeIcon icon={faChevronRight} />
        </NavArrowButton>
      </ChartHeaderNav>
      {fetchingAnalyticsData && (
        <div className="mt-4">
          <LoadingCard />
        </div>
      )}
      {!fetchingAnalyticsData && (
        <div>
          <VictoryChart
            scale={{ x: 'time' }}
            width={648}
            height={350}
            domain={{ x: domainX, y: yDomain }}
            padding={{ top: 30, right: 50, bottom: 50, left: 65 }}
            containerComponent={
              <VictoryVoronoiContainer
                voronoiBlacklist={!dataAvailable && ['socChartPlaceholder', 'boundaryLine']}
                voronoiDimension="x"
                labels={({ datum }) => (datum ? `Time: ${datum.timeLabel}\nState of charge: ${datum.y}%` : '')}
                labelComponent={
                  <VictoryTooltip
                    constrainToVisibleArea={true}
                    cornerRadius={2}
                    pointerLength={4}
                    style={{ fontSize: 16 }}
                    flyoutPadding={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    flyoutStyle={{
                      fill: theme.colors.white,
                      stroke: theme.colors.grey,
                    }}
                  />
                }
              />
            }
          >
            {!dataAvailable && (
              <VictoryLine
                name="boundaryLine"
                style={{
                  data: { stroke: 'grey', strokeWidth: 1 },
                }}
                y={() => 100}
              />
            )}
            <VictoryAxis
              dependentAxis
              tickValues={[0, 25, 50, 75, 100]}
              label="Battery state of charge (%)"
              style={{
                ticks: { stroke: 'transparent' },
                tickLabels: {
                  fill: theme.colors.black,
                  fontSize: 14,
                  padding: 5,
                },
                axis: { stroke: 'transparent' },
                axisLabel: {
                  fontSize: 17,
                  padding: 35,
                  textAnchor: 'middle',
                  fill: theme.colors.black,
                  fontWeight: 'normal',
                },
                grid: {
                  stroke: dataAvailable ? theme.colors.grey : theme.colors.white,
                  strokeWidth: 0.7,
                  strokeDasharray: '0',
                },
              }}
            />
            <VictoryAxis
              scale="time"
              tickValues={xAxisTicks}
              label="Half-hourly time period"
              tickFormat={(t, index, ticks) => {
                const str = tickTimeString(t);
                const first = index === 0;
                const last = index === ticks.length - 1;
                const major = ['06:00', '12:00', '18:00'].includes(str);
                const lastSample = lastTimeLabel && str === lastTimeLabel;
                if (first || last || major || lastSample) {
                  return str;
                }
                return '';
              }}
              style={{
                ticks: { stroke: theme.colors.light, size: 5 },
                axis: { stroke: theme.colors.light, strokeWidth: 2 },
                tickLabels: {
                  fill: theme.colors.black,
                  fontSize: 14,
                  padding: 5,
                },
                axisLabel: {
                  fontSize: 17,
                  padding: 30,
                  textAnchor: 'middle',
                  fill: theme.colors.black,
                  fontWeight: 'normal',
                },
              }}
            />
            {dataAvailable &&
              socSegments.map((segment, idx) => (
                <VictoryArea
                  key={`soc-segment-${idx}`}
                  name={`socChart-${idx}`}
                  data={padSocSegmentForVictoryArea(segment)}
                  style={{
                    data: {
                      fill: theme.colors.secondary,
                      fillOpacity: 0.1,
                      stroke: theme.colors.secondary,
                      strokeWidth: 3,
                    },
                  }}
                />
              ))}
            {!dataAvailable && (
              <VictoryArea
                name="socChartPlaceholder"
                data={placeholderSocData}
                style={{
                  data: {
                    fill: theme.colors.secondary,
                    fillOpacity: 0.1,
                    stroke: theme.colors.secondary,
                    strokeWidth: 3,
                  },
                }}
              />
            )}
            {!dataAvailable && (
              <>
                <VictoryLabel
                  textAnchor="middle"
                  style={{ fontSize: 15, fontWeight: 'bold' }}
                  x={'50%'}
                  y={175}
                  text="Not enough data to show Battery state of charge."
                />
              </>
            )}
          </VictoryChart>
        </div>
      )}
    </>
  );
};

export default withTheme(BatteryStateOfCharge);
